import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { QUESTIONS } from "@/data/quiz";

export default function QuizRunner({ answers, setAnswers, onComplete, onExit }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = QUESTIONS.length;
  const q = QUESTIONS[index];
  const selected = answers[index];
  const progress = Math.round(((index + (selected ? 1 : 0)) / total) * 100);

  const choose = (letter) => {
    const next = [...answers];
    next[index] = letter;
    setAnswers(next);
  };

  const goNext = () => {
    if (!selected) return;
    if (index === total - 1) {
      onComplete();
      return;
    }
    setDirection(1);
    setIndex((i) => i + 1);
  };

  const goBack = () => {
    if (index === 0) {
      onExit();
      return;
    }
    setDirection(-1);
    setIndex((i) => i - 1);
  };

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-14" data-testid="quiz-runner">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#5a686b]" data-testid="quiz-progress-label">
            Question {index + 1} of {total}
          </span>
          <span className="text-xs font-semibold text-[#4aa0a4]">{progress}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-[#e2ded7] overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <motion.div
            className="h-full rounded-full bg-[#4aa0a4]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {index === 0 && (
        <p className="mb-6 text-sm leading-relaxed text-[#5a686b] bg-[#fdf7e7] border border-[#f4b847]/40 rounded-xl p-4" data-testid="quiz-instructions">
          Choose the answer that feels closest to what you want at this stage of your life. If several
          appeal, choose the one you would most miss if nothing changed.
        </p>
      )}

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={index}
          custom={direction}
          initial={{ opacity: 0, x: direction * 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -24 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight leading-snug text-[#1e292b] mb-6" data-testid="quiz-question-prompt">
            {q.prompt}
          </h2>

          <fieldset>
            <legend className="sr-only">{q.prompt}</legend>
            <div className="space-y-3">
              {q.options.map((opt) => {
                const isSel = selected === opt.letter;
                return (
                  <button
                    key={opt.letter}
                    type="button"
                    onClick={() => choose(opt.letter)}
                    aria-pressed={isSel}
                    data-testid={`quiz-option-${opt.letter}`}
                    className={`w-full text-left rounded-xl border-2 p-5 sm:p-6 transition-all duration-200 weave-focus ${
                      isSel
                        ? "bg-[#eef6f6] border-[#4aa0a4] shadow-md ring-2 ring-[#4aa0a4]/25 -translate-y-0.5"
                        : "bg-white border-[#e2ded7] hover:border-[#4aa0a4] hover:bg-[#eef6f6]/40 shadow-sm"
                    }`}
                  >
                    <span className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          isSel ? "border-[#4aa0a4] bg-[#4aa0a4]" : "border-[#c7cfd0] bg-white"
                        }`}
                        aria-hidden="true"
                      >
                        {isSel && <span className="h-2 w-2 rounded-full bg-white" />}
                      </span>
                      <span className="text-base sm:text-lg leading-relaxed text-[#1e292b]">{opt.text}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          data-testid="quiz-back-button"
          className="inline-flex items-center gap-2 text-[#5a686b] hover:text-[#1e292b] font-medium px-4 py-2 rounded-lg hover:bg-[#e2ded7]/40 transition-colors weave-focus"
        >
          <ArrowLeft className="h-4 w-4" />
          {index === 0 ? "Back to start" : "Back"}
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={!selected}
          data-testid="quiz-next-button"
          className="inline-flex items-center gap-2 bg-[#4aa0a4] text-white font-medium px-6 py-3 rounded-full hover:bg-[#3b868a] transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed weave-focus"
        >
          {index === total - 1 ? "See my results" : "Next"}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </main>
  );
}
